import React, { useState, useEffect } from 'react';
import { ticketsAPI } from '../lib/api';

const statusLabels = {
  aberto: { label: 'Aberto', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  em_atendimento: { label: 'Em Atendimento', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  aguardando_resposta: { label: 'Aguardando Resposta', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  resolvido: { label: 'Resolvido', color: 'bg-green-100 text-green-700 border-green-200' },
  fechado: { label: 'Fechado', color: 'bg-gray-100 text-gray-700 border-gray-200' },
};

const prioridadeLabels = {
  baixa: { label: 'Baixa', color: 'bg-gray-100 text-gray-600' },
  media: { label: 'Média', color: 'bg-blue-100 text-blue-600' },
  alta: { label: 'Alta', color: 'bg-orange-100 text-orange-600' },
  urgente: { label: 'Urgente', color: 'bg-red-100 text-red-600' },
};

const Tickets = () => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [resposta, setResposta] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [ticketsRes, statsRes] = await Promise.all([
        ticketsAPI.list(),
        ticketsAPI.getStats()
      ]);
      setTickets(ticketsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Erro ao carregar tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerDetalhes = async (ticketId) => {
    try {
      const res = await ticketsAPI.get(ticketId);
      setSelectedTicket(res.data);
    } catch (error) {
      alert('Erro ao carregar detalhes do ticket');
    }
  };

  const handleResponder = async () => {
    if (!resposta.trim() || !selectedTicket) return;
    
    try {
      setEnviando(true);
      await ticketsAPI.responder(selectedTicket.id, resposta);
      setResposta('');
      // Recarregar ticket
      const res = await ticketsAPI.get(selectedTicket.id);
      setSelectedTicket(res.data);
      loadData();
    } catch (error) {
      alert('Erro ao enviar resposta');
    } finally {
      setEnviando(false);
    }
  };

  const handleUpdateStatus = async (ticketId, newStatus) => {
    try {
      await ticketsAPI.updateStatus(ticketId, newStatus);
      if (selectedTicket && selectedTicket.id === ticketId) {
        const res = await ticketsAPI.get(ticketId);
        setSelectedTicket(res.data);
      }
      loadData();
    } catch (error) {
      alert('Erro ao atualizar status');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const filteredTickets = tickets.filter(t => {
    if (filter === 'all') return true;
    if (filter === 'pendentes') return ['aberto', 'em_atendimento'].includes(t.status);
    return t.status === filter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm border">
            <p className="text-xs text-gray-500 mb-1">Total</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-blue-200">
            <p className="text-xs text-blue-600 mb-1">Abertos</p>
            <p className="text-2xl font-bold text-blue-700">{stats.por_status?.aberto || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-yellow-200">
            <p className="text-xs text-yellow-600 mb-1">Em Atendimento</p>
            <p className="text-2xl font-bold text-yellow-700">{stats.por_status?.em_atendimento || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-purple-200">
            <p className="text-xs text-purple-600 mb-1">Aguardando</p>
            <p className="text-2xl font-bold text-purple-700">{stats.por_status?.aguardando_resposta || 0}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-red-200">
            <p className="text-xs text-red-600 mb-1">Urgentes</p>
            <p className="text-2xl font-bold text-red-700">{stats.por_prioridade?.urgente || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {[
          { key: 'all', label: 'Todos' },
          { key: 'pendentes', label: 'Pendentes' },
          { key: 'aberto', label: 'Abertos' },
          { key: 'em_atendimento', label: 'Em Atendimento' },
          { key: 'aguardando_resposta', label: 'Aguardando' },
          { key: 'resolvido', label: 'Resolvidos' },
          { key: 'fechado', label: 'Fechados' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === f.key
                ? 'bg-emerald-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100 border'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Tickets Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Ticket</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Tenant</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Usuário</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Prioridade</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Data</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredTickets.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    Nenhum ticket encontrado
                  </td>
                </tr>
              ) : (
                filteredTickets.map((ticket) => (
                  <tr key={ticket.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900 truncate max-w-[200px]">{ticket.titulo}</p>
                        <p className="text-xs text-gray-500">{ticket.categoria_nome || '-'}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-emerald-600 font-medium">{ticket.tenant_nome || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm text-gray-900">{ticket.usuario_nome}</p>
                        <p className="text-xs text-gray-500">{ticket.usuario_email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${prioridadeLabels[ticket.prioridade]?.color || ''}`}>
                        {prioridadeLabels[ticket.prioridade]?.label || ticket.prioridade}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${statusLabels[ticket.status]?.color || ''}`}>
                        {statusLabels[ticket.status]?.label || ticket.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {formatDate(ticket.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleVerDetalhes(ticket.id)}
                        className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                        title="Ver detalhes"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Detalhes */}
      {selectedTicket && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl w-full max-w-2xl shadow-xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="p-6 border-b flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${prioridadeLabels[selectedTicket.prioridade]?.color || ''}`}>
                    {prioridadeLabels[selectedTicket.prioridade]?.label || selectedTicket.prioridade}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusLabels[selectedTicket.status]?.color || ''}`}>
                    {statusLabels[selectedTicket.status]?.label || selectedTicket.status}
                  </span>
                </div>
                <h2 className="text-xl font-bold text-gray-900">{selectedTicket.titulo}</h2>
                <p className="text-sm text-gray-500">
                  <span className="text-emerald-600 font-medium">{selectedTicket.tenant_nome}</span> • {selectedTicket.usuario_nome} • {formatDate(selectedTicket.created_at)}
                </p>
              </div>
              <button
                onClick={() => setSelectedTicket(null)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Descrição original */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedTicket.descricao}</p>
              </div>

              {/* Mensagens */}
              {selectedTicket.mensagens?.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-gray-500">Conversas</h4>
                  {selectedTicket.mensagens.map((msg) => (
                    <div
                      key={msg.id}
                      className={`p-3 rounded-lg ${
                        msg.is_admin
                          ? 'bg-emerald-50 border border-emerald-200 ml-4'
                          : 'bg-gray-50 border border-gray-200 mr-4'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`font-medium text-sm ${msg.is_admin ? 'text-emerald-700' : 'text-gray-700'}`}>
                          {msg.is_admin ? 'Suporte AgroLink' : msg.usuario_nome}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(msg.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{msg.mensagem}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer - Ações */}
            <div className="p-4 border-t space-y-4">
              {/* Alterar Status */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Alterar Status:</span>
                <div className="flex flex-wrap gap-1">
                  {['em_atendimento', 'aguardando_resposta', 'resolvido', 'fechado'].map(status => (
                    <button
                      key={status}
                      onClick={() => handleUpdateStatus(selectedTicket.id, status)}
                      disabled={selectedTicket.status === status}
                      className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${
                        selectedTicket.status === status
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:opacity-80'
                      } ${statusLabels[status]?.color || ''}`}
                    >
                      {statusLabels[status]?.label || status}
                    </button>
                  ))}
                </div>
              </div>

              {/* Campo de Resposta */}
              {selectedTicket.status !== 'fechado' && selectedTicket.status !== 'resolvido' && (
                <div className="flex gap-2">
                  <textarea
                    value={resposta}
                    onChange={(e) => setResposta(e.target.value)}
                    placeholder="Digite sua resposta..."
                    rows={2}
                    className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                  />
                  <button
                    onClick={handleResponder}
                    disabled={!resposta.trim() || enviando}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {enviando ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    )}
                    Enviar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tickets;
